import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { CheckCircle2, Circle, X, ArrowRight } from "lucide-react";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";

// Where each checklist step is completed. "first_sync" and "invite" are actions, not pages.
const STEP_LINKS = {
  cms: "/dashboard/settings/cms",
  site_url: "/dashboard/settings/business",
  gsc: "/dashboard/settings/search-console",
  business: "/dashboard/settings/business",
  first_ai_action: "/dashboard/fanout",
};

// Onboarding checklist on Overview. Status comes from GET /onboarding, which derives each
// step from real data. Hidden once every step is done or the org dismisses it.
export default function SetupChecklist({ onSync, syncing, refreshKey }) {
  const navigate = useNavigate();
  const { openOrganizationProfile } = useClerk();
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api.getOnboarding());
    } catch (e) {
      console.error("[onboarding] status load failed:", e);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!status || status.dismissed || status.all_done) return null;

  const done = status.steps.filter((s) => s.done).length;
  const cmsDone = status.steps.find((s) => s.key === "cms")?.done;
  const next = status.steps.find((s) => !s.done)?.key;

  async function dismiss() {
    setStatus((s) => ({ ...s, dismissed: true }));
    try {
      await api.dismissOnboarding();
    } catch (e) {
      console.error("[onboarding] dismiss failed:", e);
    }
  }

  function act(step) {
    if (step.key === "first_sync") return onSync?.();
    if (step.key === "invite") return openOrganizationProfile();
    navigate(STEP_LINKS[step.key]);
  }

  return (
    <div className="mb-6 bg-white border border-[#018D4C]/20 rounded-xl p-5" data-testid="setup-checklist">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold" style={{ color: PRIMARY }}>Set up your workspace</p>
          <p className="text-xs text-zinc-500 mt-0.5">{done} of {status.steps.length} done</p>
        </div>
        {status.complete && (
          <button onClick={dismiss} title="Hide checklist" className="p-1 text-zinc-400 hover:text-zinc-900">
            <X size={14} />
          </button>
        )}
      </div>
      <div className="mt-3 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${(done / status.steps.length) * 100}%`, backgroundColor: PRIMARY }} />
      </div>
      <ul className="mt-4 divide-y divide-zinc-100">
        {status.steps.map((step) => {
          const blocked = step.key === "first_sync" && !cmsDone;
          return (
            <li key={step.key} className="flex items-center gap-3 py-2.5">
              {step.done
                ? <CheckCircle2 size={16} style={{ color: PRIMARY }} />
                : <Circle size={16} className="text-zinc-300" />}
              <span className={`flex-1 text-sm ${step.done ? "text-zinc-400 line-through" : "text-zinc-900"}`}>
                {step.title}
                {step.required && !step.done && <span className="ml-2 text-[10px] font-bold text-zinc-400">REQUIRED</span>}
                {step.advanced && !step.done && <span className="ml-2 text-[10px] font-bold text-zinc-400">ADVANCED · OPTIONAL</span>}
              </span>
              {!step.done && (
                <button
                  onClick={() => act(step)}
                  disabled={blocked || (step.key === "first_sync" && syncing)}
                  title={blocked ? "Connect your CMS first" : undefined}
                  className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-40 ${
                    step.key === next ? "text-white" : "text-zinc-700 border border-zinc-200 hover:bg-zinc-50"
                  }`}
                  style={step.key === next ? { backgroundColor: PRIMARY } : undefined}
                >
                  {step.key === "first_sync" ? (syncing ? "Syncing…" : "Sync now") : step.key === "invite" ? "Invite" : "Set up"}
                  <ArrowRight size={12} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
